import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

// Get 45 days range (from 45 days ago to today)
const get45DaysRange = () => {
  const endDate = dayjs().endOf('day');
  const startDate = dayjs().subtract(45, 'days').startOf('day');
  return [startDate, endDate];
};

// Initial state
const initialState = {
  orders: [],
  orderItems: [],
  filteredOrderItems: [],
  selectedOrder: null,
  loading: false,
  error: null,
  syncLoading: false,
  syncError: null,
  kpiMetrics: null,
  pagination: {
    current: 1,
    pageSize: 30,
    total: 0
  },
  filters: {
    searchText: '',
    selectedStatus: [],
    selectedDateRange: get45DaysRange(),
    selectedStore: [],
    selectedFinancialStatus: [],
    selectedFulfillmentStatus: [],
    selectedShipmentStatus: [],
    selectedShipmentDetail: 'all',
    selectedConfirmShipment: 'all',
    selectedCategory: [],
    selectedCollection: [],
    selectedPrinter: [],
    selectedSupporter: [],
    selectedTitle: '',
    selectedSku: '',
    selectedEmployeeId: '',
    selectedExcludeTitle: '',
    selectedExcludeSku: '',
    selectedBasecost: 'all'
  },
};

// ShopifyOrders slice
const shopifyOrdersSlice = createSlice({
  name: 'shopifyOrders',
  initialState,
  reducers: {
    setOrders: (state, action) => {
      state.orders = action.payload;
    },
    setOrderItems: (state, action) => {
      state.orderItems = action.payload;
      state.filteredOrderItems = action.payload;
    },
    setFilteredOrderItems: (state, action) => {
      state.filteredOrderItems = action.payload;
    },
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSyncLoading: (state, action) => {
      state.syncLoading = action.payload;
    },
    setSyncError: (state, action) => {
      state.syncError = action.payload;
    },
    setKpiMetrics: (state, action) => {
      state.kpiMetrics = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        searchText: '',
        selectedStatus: [],
        selectedDateRange: null,
        selectedStore: [],
        selectedFinancialStatus: [],
        selectedFulfillmentStatus: [],
        selectedShipmentStatus: [],
        selectedShipmentDetail: 'all',
        selectedConfirmShipment: 'all',
        selectedCategory: [],
        selectedCollection: [],
        selectedPrinter: [],
        selectedSupporter: [],
        selectedTitle: '',
        selectedSku: '',
        selectedExcludeTitle: '',
        selectedExcludeSku: '',
        selectedBasecost: 'all'
      };
    },
    updateOrderItem: (state, action) => {
      const { id, field, value, orderField } = action.payload;
      
      // Update in orderItems array
      const orderItemIndex = state.orderItems.findIndex(item => item.id === id);
      if (orderItemIndex !== -1) {
        if (orderField && state.orderItems[orderItemIndex].order) {
          state.orderItems[orderItemIndex].order[field] = value;
        } else {
          state.orderItems[orderItemIndex][field] = value;
        }
      }
      
      // Update in filteredOrderItems array
      const filteredItemIndex = state.filteredOrderItems.findIndex(item => item.id === id);
      if (filteredItemIndex !== -1) {
        if (orderField && state.filteredOrderItems[filteredItemIndex].order) {
          state.filteredOrderItems[filteredItemIndex].order[field] = value;
        } else {
          state.filteredOrderItems[filteredItemIndex][field] = value;
        }
      }
    },
  },
});

export const {
  setOrders,
  setOrderItems,
  setFilteredOrderItems,
  setSelectedOrder,
  setLoading,
  setError,
  setSyncLoading,
  setSyncError,
  setKpiMetrics,
  setPagination,
  setFilters,
  clearFilters,
  updateOrderItem,
} = shopifyOrdersSlice.actions;

export default shopifyOrdersSlice.reducer;
